import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Btn } from '../../components/Btn';
import { Card } from '../../components/Card';
import { PageHeader } from '../../components/PageHeader';
import { ProductSelect } from '../../components/ProductSelect';
import {
  listBranches,
  listProducts,
  listStockByBranch,
} from '../../data/fetchers';
import { addStockMovement } from '../../data/mutators';
import { fmtAmount, fmtStockBreakdown } from '../../lib/format';
import { visibleBranchIds } from '../../lib/permissions';
import { toBaseQty } from '../../lib/stockUnits';
import { useSession } from '../../stores/session';

export function StockAdjustView() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useSession((s) => s.user);
  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });
  const { data: products = [] } = useQuery({
    queryKey: ['products'],
    queryFn: listProducts,
  });
  const allowed = user
    ? visibleBranchIds(
        user,
        branches.map((b) => b.id),
      )
    : [];
  const visible = branches.filter((b) => allowed.includes(b.id));

  const [branchId, setBranchId] = useState('');
  const [productId, setProductId] = useState('');
  const [unit, setUnit] = useState('');
  const [dir, setDir] = useState<'add' | 'remove'>('add');
  const [qty, setQty] = useState(1);
  const [reason, setReason] = useState('ตรวจนับพบเพิ่ม');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  // Scanner & Barcode states
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [barcodeQuery, setBarcodeQuery] = useState('');
  const [scanFeedback, setScanFeedback] = useState('');

  const product = products.find((p) => p.id === productId) ?? null;
  // Default the unit to the product's first unit whenever the product changes.
  useEffect(() => {
    setUnit(product?.units[0]?.unit ?? '');
  }, [product]);

  const { data: stock = [] } = useQuery({
    queryKey: ['stock', branchId],
    queryFn: () => listStockByBranch({ branchId: branchId || null }),
    enabled: !!branchId,
  });
  const onHand =
    branchId && productId
      ? (stock.find((s) => s.productId === productId)?.onHand ?? 0)
      : 0;

  const mutation = useMutation({
    mutationFn: (movement: Parameters<typeof addStockMovement>[0]) =>
      addStockMovement(movement),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stock'] });
      void qc.invalidateQueries({ queryKey: ['movements'] });
      navigate('/stock');
    },
  });

  const playBeep = () => {
    try {
      const AudioCtx =
        window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(1000, ctx.currentTime);
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.08);
    } catch (e) {
      console.warn('Audio beep failed', e);
    }
  };

  const handleBarcodeSearch = (code: string) => {
    let found = false;
    for (const p of products) {
      for (const u of p.units) {
        if (u.barcode === code.trim()) {
          setProductId(p.id);
          setUnit(u.unit);
          found = true;
          playBeep();
          setScanFeedback(`✓ สแกนพบสินค้า: ${p.name} (${u.unit})`);
          setTimeout(() => setScanFeedback(''), 5000);
          break;
        }
      }
      if (found) break;
    }
    if (!found) {
      setScanFeedback(`❌ ไม่พบบาร์โค้ดรหัส: "${code}"`);
      setTimeout(() => setScanFeedback(''), 5000);
    }
  };

  useEffect(() => {
    if (!isScanning) return;

    let stream: MediaStream | null = null;
    let scanInterval: any = null;

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' } })
      .then((s) => {
        stream = s;
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          videoRef.current
            .play()
            .catch((e) => console.warn('Video play error', e));
        }

        if ('BarcodeDetector' in window) {
          const detector = new (window as any).BarcodeDetector({
            formats: ['ean_13', 'ean_8', 'qr_code', 'code_128'],
          });

          scanInterval = setInterval(async () => {
            if (
              videoRef.current &&
              videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA
            ) {
              try {
                const codes = await detector.detect(videoRef.current);
                if (codes.length > 0) {
                  const raw = codes[0].rawValue;
                  handleBarcodeSearch(raw);
                  setIsScanning(false);
                }
              } catch (e) {
                console.error('Barcode detection error', e);
              }
            }
          }, 400);
        }
      })
      .catch((err) => {
        console.error('Camera access error', err);
        setCameraError(
          'ไม่สามารถเข้าถึงกล้องถ่ายรูปได้ โปรดตรวจสอบสิทธิ์การเข้าใช้งานกล้องในเบราว์เซอร์ของคุณ',
        );
      });

    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
      if (scanInterval) {
        clearInterval(scanInterval);
      }
    };
  }, [isScanning]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!branchId || !product || !unit) return;
    if (qty <= 0) {
      setError('จำนวนต้องมากกว่า 0');
      return;
    }
    const selectedUnit = product.units.find((u) => u.unit === unit);
    if (!selectedUnit?.id) return;
    const base = toBaseQty(product, unit, qty);
    if (dir === 'remove' && base > onHand) {
      setError(`หักออกได้ไม่เกินคงเหลือ (${onHand})`);
      return;
    }
    const signedQty = dir === 'add' ? qty : -qty;
    mutation.mutate({
      branchId,
      productId,
      requestId: crypto.randomUUID(),
      unitId: selectedUnit.id,
      type: 'adjust',
      qty: signedQty,
      reason,
      note,
    });
  };

  return (
    <div className="bo-fade w-full">
      <PageHeader
        title="ปรับสต๊อก"
        subtitle="เพิ่มหรือลดจำนวนคงเหลือพร้อมเหตุผล"
      />

      <form onSubmit={onSubmit} className="w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* Left Column: Form Info */}
          <div className="lg:col-span-7 flex flex-col gap-4">
            <Card title="ข้อมูลการปรับสต๊อก">
              <div className="flex flex-col gap-4">
                {/* Branch Selection */}
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm-label font-semibold text-ink-2">
                    สาขาที่ทำรายการ <span className="text-danger">*</span>
                  </span>
                  <select
                    aria-label="สาขา"
                    value={branchId}
                    onChange={(e) => setBranchId(e.target.value)}
                    className="h-10 px-3 rounded-btn border border-line bg-surface text-body focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  >
                    <option value="">— เลือกสาขา —</option>
                    {visible.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.short}
                      </option>
                    ))}
                  </select>
                </label>

                {/* Barcode scan section */}
                <div className="flex flex-col gap-1.5">
                  <span className="text-sm-label font-semibold text-ink-2">
                    ค้นหาด้วยบาร์โค้ด / สแกนด่วน
                  </span>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        placeholder="กรอกรหัสบาร์โค้ด..."
                        value={barcodeQuery}
                        onChange={(e) => setBarcodeQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (barcodeQuery) handleBarcodeSearch(barcodeQuery);
                          }
                        }}
                        className="h-10 w-full pl-3 pr-10 rounded-btn border border-line bg-surface text-body focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all num"
                      />
                      {barcodeQuery && (
                        <button
                          type="button"
                          onClick={() => setBarcodeQuery('')}
                          className="absolute right-3 top-2.5 text-ink-3 hover:text-ink"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    <Btn
                      type="button"
                      kind="default"
                      onClick={() => handleBarcodeSearch(barcodeQuery)}
                      disabled={!barcodeQuery}
                    >
                      ค้นหา
                    </Btn>
                    <Btn
                      type="button"
                      kind="primary"
                      className="bg-accent hover:bg-accent-dark text-white shadow-sm"
                      onClick={() => {
                        setIsScanning(true);
                        setCameraError('');
                      }}
                      icon={
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 4v1m6 11h2m-6 0h-2v4m0-16v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
                          />
                        </svg>
                      }
                    >
                      สแกนกล้อง
                    </Btn>
                  </div>
                  {scanFeedback && (
                    <div
                      className={`text-xs font-semibold px-3 py-2 rounded-btn mt-1 transition-all ${
                        scanFeedback.startsWith('✓')
                          ? 'bg-ok-light text-ok border border-ok/10'
                          : 'bg-danger-light text-danger border border-danger/10'
                      }`}
                    >
                      {scanFeedback}
                    </div>
                  )}
                </div>

                {/* Product Selection with Search Combobox */}
                <div className="flex flex-col gap-1.5">
                  <span className="text-sm-label font-semibold text-ink-2">
                    สินค้า <span className="text-danger">*</span>
                  </span>
                  <ProductSelect
                    products={products}
                    value={productId}
                    onChange={setProductId}
                  />
                </div>

                {/* Direction, Qty, and Unit */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <label className="flex flex-col gap-1.5">
                    <span className="text-sm-label font-semibold text-ink-2">
                      ทิศทาง
                    </span>
                    <select
                      aria-label="ทิศทาง"
                      value={dir}
                      onChange={(e) =>
                        setDir(e.target.value as 'add' | 'remove')
                      }
                      className="h-10 px-3 rounded-btn border border-line bg-surface text-body focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    >
                      <option value="add">เพิ่มสต๊อก (+)</option>
                      <option value="remove">หักออก (−)</option>
                    </select>
                  </label>

                  <label className="flex flex-col gap-1.5">
                    <span className="text-sm-label font-semibold text-ink-2">
                      จำนวน
                    </span>
                    <input
                      aria-label="จำนวน"
                      type="number"
                      min={1}
                      value={qty}
                      onChange={(e) => setQty(Number(e.target.value))}
                      className="h-10 px-3 rounded-btn border border-line bg-surface text-body num focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    />
                  </label>

                  <label className="flex flex-col gap-1.5">
                    <span className="text-sm-label font-semibold text-ink-2">
                      หน่วย
                    </span>
                    <select
                      aria-label="หน่วย"
                      value={unit}
                      onChange={(e) => setUnit(e.target.value)}
                      disabled={!product}
                      className="h-10 px-3 rounded-btn border border-line bg-surface text-body focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all disabled:bg-bg-warm disabled:text-ink-3"
                    >
                      {(product?.units ?? []).map((u) => (
                        <option key={u.unit} value={u.unit}>
                          {u.unit}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                {/* Reason & Notes */}
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm-label font-semibold text-ink-2">
                    เหตุผลการปรับปรุงสต๊อก
                  </span>
                  <select
                    aria-label="เหตุผล"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="h-10 px-3 rounded-btn border border-line bg-surface text-body focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  >
                    <option>ตรวจนับพบเพิ่ม</option>
                    <option>สินค้าชำรุด/แตกหัก</option>
                    <option>หมดอายุ</option>
                    <option>ของสูญหาย</option>
                    <option>อื่น ๆ</option>
                  </select>
                </label>

                <label className="flex flex-col gap-1.5">
                  <span className="text-sm-label font-semibold text-ink-2">
                    หมายเหตุ
                  </span>
                  <input
                    aria-label="หมายเหตุ"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="ระบุรายละเอียดการปรับปรุง..."
                    className="h-10 px-3 rounded-btn border border-line bg-surface text-body focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  />
                </label>
              </div>
            </Card>

            {error && (
              <div className="text-xs text-danger font-semibold bg-danger-light p-3 rounded-btn border border-danger/10 leading-relaxed">
                ⚠️ {error}
              </div>
            )}

            <div className="flex gap-3">
              <Btn
                kind="default"
                type="button"
                onClick={() => navigate('/stock')}
                className="flex-1"
              >
                ยกเลิก
              </Btn>
              <Btn
                kind="primary"
                type="submit"
                disabled={
                  !branchId ||
                  !product ||
                  !unit ||
                  qty <= 0 ||
                  mutation.isPending
                }
                className="flex-1"
              >
                {mutation.isPending ? 'กำลังบันทึก...' : 'บันทึก'}
              </Btn>
            </div>
          </div>

          {/* Right Column: Inventory Preview Detail */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            {product ? (
              <Card title="รายละเอียดคงเหลือ & บาร์โค้ด">
                <div className="flex flex-col gap-4 text-body text-ink-2">
                  <div>
                    <div className="text-sm-label text-ink-3">ชื่อสินค้า</div>
                    <div className="font-bold text-ink text-base">
                      {product.name}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm-label text-ink-3">หน่วยฐาน</div>
                      <div className="font-semibold text-ink">
                        {product.baseUnit}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm-label text-ink-3">
                        ต้นทุนล่าสุด
                      </div>
                      <div className="font-semibold text-ink num">
                        ฿{fmtAmount(product.cost ?? 0)}
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-line-2 my-1"></div>

                  <div>
                    <div className="text-sm-label text-ink-3 mb-2">
                      บาร์โค้ดและหน่วยสินค้า
                    </div>
                    <div className="flex flex-col gap-2">
                      {product.units.map((u) => (
                        <div
                          key={u.unit}
                          className="flex justify-between items-center bg-bg-warm/50 px-3 py-2 rounded-btn border border-line-2"
                        >
                          <div>
                            <div className="font-semibold text-ink text-xs">
                              {u.unit}{' '}
                              <span className="text-ink-3 font-normal">
                                (×{u.factor})
                              </span>
                            </div>
                            <div className="text-[11px] text-ink-3 font-mono">
                              {u.barcode || 'ไม่มีบาร์โค้ด'}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-primary font-bold">
                              ฿{fmtAmount(u.price)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="border-t border-line-2 my-1"></div>

                  <div>
                    <div className="text-sm-label text-ink-3">
                      สต๊อกคงเหลือปัจจุบัน
                    </div>
                    <div className="text-h2 font-black text-ink-2 mt-1 num">
                      {fmtStockBreakdown(onHand, product)}
                    </div>
                  </div>
                </div>
              </Card>
            ) : (
              <Card title="รายละเอียดคงเหลือ & บาร์โค้ด">
                <div className="text-center py-16 text-ink-3 text-body bg-surface-2/20 border border-dashed border-line-2 rounded-btn leading-relaxed">
                  เลือกสาขาและสินค้า
                  เพื่อดูรายละเอียดคงเหลือรายหน่วยและข้อมูลรหัสบาร์โค้ดของผลิตภัณฑ์
                </div>
              </Card>
            )}
          </div>
        </div>
      </form>

      {/* Camera scan fullscreen interface */}
      {isScanning && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center">
          <style
            dangerouslySetInnerHTML={{
              __html: `
            @keyframes scanLaser {
              0% { top: 0%; }
              50% { top: 100%; }
              100% { top: 0%; }
            }
          `,
            }}
          />

          {/* Scanning Guide Overlay */}
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-between py-8 px-4 text-center">
            <div className="flex justify-between items-center w-full max-w-md">
              <h3 className="text-white text-base font-extrabold">
                สแกนบาร์โค้ดสินค้า
              </h3>
              <button
                type="button"
                onClick={() => setIsScanning(false)}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors text-lg"
              >
                ✕
              </button>
            </div>

            {/* Scanning Target Box */}
            <div className="relative w-64 h-64 border-2 border-white/40 rounded-xl overflow-hidden shadow-[0_0_0_2000px_rgba(0,0,0,0.6)]">
              {/* Laser Line */}
              <div className="absolute left-0 right-0 h-0.5 bg-danger shadow-[0_0_8px_rgba(200,54,42,0.8)] animate-[scanLaser_2s_infinite]"></div>
            </div>

            <div className="text-white text-sm font-semibold max-w-xs leading-relaxed bg-black/40 py-2 px-4 rounded-chip">
              วางบาร์โค้ดให้อยู่ในกรอบสี่เหลี่ยมเพื่อสแกนอัตโนมัติ
            </div>
          </div>

          {/* Camera Video Feed */}
          <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-cover"
            playsInline
            muted
          />

          {cameraError && (
            <div className="absolute inset-0 bg-black/90 flex flex-col items-center justify-center p-6 text-center z-30">
              <span className="text-danger text-lg font-bold mb-2">
                เกิดข้อผิดพลาดในการโหลดกล้อง
              </span>
              <span className="text-white text-sm mb-6 max-w-sm">
                {cameraError}
              </span>
              <Btn kind="default" onClick={() => setIsScanning(false)}>
                ปิดหน้าต่างสแกน
              </Btn>
            </div>
          )}

          {/* Simulated Scanner Fallback */}
          {!('BarcodeDetector' in window) && !cameraError && (
            <div className="absolute bottom-6 left-4 right-4 bg-black/90 text-white p-4 rounded-xl text-xs flex flex-col gap-2.5 z-20 border border-white/10 max-w-md mx-auto">
              <div className="flex gap-1.5 items-center text-warn font-bold">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <span>เบราว์เซอร์ไม่รองรับตัวตรวจจับบาร์โค้ดด่วนในกล้องสด</span>
              </div>
              <span className="text-white/60">
                คุณสามารถค้นหาด้วยการพิมพ์รหัสบาร์โค้ด
                หรือทดสอบโดยการคลิกจำลองรายการสแกนดังต่อไปนี้:
              </span>
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    handleBarcodeSearch(e.target.value);
                    setIsScanning(false);
                  }
                }}
                className="bg-white/10 border border-white/20 text-white h-9 px-3 rounded-btn text-xs w-full focus:outline-none"
              >
                <option value="" className="bg-ink">
                  --- เลือกสินค้าเพื่อจำลองการสแกน ---
                </option>
                {products.map((p) =>
                  p.units.map((u) => (
                    <option
                      key={u.barcode}
                      value={u.barcode}
                      className="bg-ink"
                    >
                      {p.name} ({u.unit}) - {u.barcode}
                    </option>
                  )),
                )}
              </select>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
